import {
  ComponentRef,
  Injectable,
  Type,
  ViewContainerRef,
} from '@angular/core';
import {
  hasOnLoadedImplementation,
  OnLoaded,
  OnLoadedComponent,
} from './on-loaded';

@Injectable({
  providedIn: 'root',
})
export class DynamicComponentLoader {
  /**
   * Inject a new instance of the given type and injects it as replacement for
   * the viewContainers content.
   * @param viewContainerRef
   * @param component
   */
  public injectReplacementComponent<T extends OnLoaded>(
    viewContainerRef: ViewContainerRef,
    component: OnLoadedComponent,
  ): T {
    return this.componentInjection<T>(viewContainerRef, component, true);
  }

  /**
   * Searches the viewContainer for elements using the given selector. Each found
   * element is replaced with a new instance of the given type.
   */
  public injectReplacementComponentByElement<T extends OnLoaded>(
    viewContainerRef: ViewContainerRef,
    component: OnLoadedComponent,
    selector: string,
  ): T[] {
    const viewElement: HTMLElement = viewContainerRef.element.nativeElement;

    const createdInstances: T[] = [];
    for (const element of Array.from(viewElement.querySelectorAll(selector))) {
      const componentRef: ComponentRef<T> = viewContainerRef.createComponent<T>(
        component as Type<never>,
      );
      componentRef.changeDetectorRef.markForCheck();

      const instance: T = componentRef.instance;

      if (hasOnLoadedImplementation(instance) === false) {
        throw new Error('component is missing OnLoaded interface');
      }

      createdInstances.push(instance);

      element.replaceWith(componentRef.location.nativeElement);
    }

    return createdInstances;
  }

  /**
   * This method inject a new instance in the view Container
   */
  private componentInjection<T extends OnLoaded>(
    viewContainerRef: ViewContainerRef,
    component: OnLoadedComponent,
    clearViewContainer: boolean = false,
  ): T {
    if (clearViewContainer) {
      viewContainerRef.clear();
    }

    const componentRef: ComponentRef<T> = viewContainerRef.createComponent<T>(
      component as Type<never>,
    );
    componentRef.changeDetectorRef.markForCheck();

    const instance: T = componentRef.instance;

    if (hasOnLoadedImplementation(instance) === false) {
      throw new Error('component is missing OnLoaded interface');
    }

    return instance;
  }
}
